import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import DataTable, { Column } from '../../../components/DataTable/DataTable';
import { fetcher } from '../../../services/fetcher';
import { useTheme } from '../../../contexts/ThemeContext';
import { Badge, Button, Card, DateInput, DocumentTextIcon, SearchIcon, Select, Switch, TextInput } from '../../../components/ui';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { cn } from '../../../lib/utils';
import { ACCOUNTING_TABLE_DEFAULTS } from '../shared/typography';
import { AccountingReportShell } from './shared/AccountingReportShell';
import { FieldLabel, formatReportAmount } from './shared/reportUtils';
import { buildReportQuery } from './shared/useAccountingReportFilters';
import { useAccCompanies } from '../companies/useData';

type Row = {
  companyId: number;
  companyName: string;
  companyCode: string;
  accountId: number;
  accountCode: string;
  accountName: string;
  linkNumber: string;
  debitTotal: number;
  creditTotal: number;
  debitTotalUSD: number;
  creditTotalUSD: number;
  balance: number;
  balanceUSD: number;
  currencyCode: string | null;
  earliestDueDate: string | null;
  daysPastDue?: number;
  agingBucket?: string;
};

export const OpenItemsDetail: React.FC = () => {
  const { colors } = useTheme();
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().slice(0, 10));
  const [companyId, setCompanyId] = useState('');
  const [linkFilter, setLinkFilter] = useState('');
  const [showAging, setShowAging] = useState(true);
  const [run, setRun] = useState(false);

  const { data: companiesRaw } = useAccCompanies('', 1, 500, {
    filters: { filters: { isActive: ['true'] } },
  });
  const companyOptions = useMemo(
    () => [
      { value: '', label: 'All companies' },
      ...(companiesRaw ?? []).map((c) => ({ value: String(c.id), label: c.name })),
    ],
    [companiesRaw],
  );

  const reportUrl =
    run
      ? buildReportQuery('/accounting/reports/open-items', {
          asOfDate,
          companyId: companyId || undefined,
          linkNumber: linkFilter || undefined,
          includeAging: showAging,
        })
      : null;

  const { data: rows, isLoading } = useSWR<Row[]>(reportUrl, fetcher);
  const data = rows ?? [];

  const columns = useMemo<Column<Row>[]>(
    () => [
      { key: 'companyCode', header: 'Company', sortable: true },
      { key: 'accountCode', header: 'Account', sortable: true, render: (_v, row) => row.accountName },
      { key: 'linkNumber', header: 'Link #', sortable: true },
      { key: 'balance', header: 'Balance', align: 'right', render: (v: number) => formatReportAmount(v) },
      { key: 'balanceUSD', header: 'Balance USD', align: 'right', render: (v: number) => formatReportAmount(v) },
      { key: 'currencyCode', header: 'CCY', render: (v) => v ?? '—' },
      { key: 'earliestDueDate', header: 'Due', render: (v) => v ?? '—' },
      ...(showAging
        ? [
            { key: 'daysPastDue', header: 'Days', align: 'right' as const },
            { key: 'agingBucket', header: 'Bucket' },
          ]
        : []),
      {
        key: 'actions',
        header: '',
        width: '3rem',
        render: (_v, row) => (
          <Link
            to={`/accounting/reports/link-statement?companyId=${row.companyId}&accountId=${row.accountId}&linkNumber=${encodeURIComponent(row.linkNumber)}&dateTo=${asOfDate}&dateFrom=2000-01-01&from=open-items`}
            className="inline-flex p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800"
            title="Link statement"
          >
            <DocumentTextIcon className="h-4 w-4" />
          </Link>
        ),
      },
    ],
    [showAging, asOfDate],
  );

  const toolbar = (
    <Card className="p-4 shrink-0 space-y-3">
      <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
        <div>
          <FieldLabel>As of date</FieldLabel>
          <DateInput value={asOfDate} onChange={(e) => { setAsOfDate(e.target.value); setRun(false); }} showFormatHint={false} />
        </div>
        <div className="min-w-[12rem]">
          <FieldLabel>Company</FieldLabel>
          <Select value={companyId} onChange={(e) => { setCompanyId(e.target.value); setRun(false); }} options={companyOptions} />
        </div>
        <div className="min-w-[10rem]">
          <FieldLabel>Link #</FieldLabel>
          <TextInput value={linkFilter} onChange={(e) => { setLinkFilter(e.target.value); setRun(false); }} placeholder="Filter…" />
        </div>
        <Switch label="Show aging" checked={showAging} onChange={(e) => { setShowAging(e.target.checked); setRun(false); }} size="sm" />
      </div>
      <div className={cn('flex items-center gap-2 pt-3 border-t', colors.border)}>
        <Button variant="default" onClick={() => setRun(true)} className="h-10 px-5 gap-2 ml-auto">
          <SearchIcon className="h-4 w-4" />
          Search
        </Button>
      </div>
    </Card>
  );

  return (
    <AccountingReportShell
      toolbar={toolbar}
      run={run}
      loading={isLoading}
      rows={data}
      columns={columns}
      entityName="Open Items"
      exportFileBase="open-items"
      exportDateTo={asOfDate}
      statusBadge={
        <Badge variant="info" className="font-normal">
          As of {formatDateGlobal(asOfDate)}
        </Badge>
      }
      recordCount={data.length}
      readyMessage="Set filters and click Search to list open items."
    >
      <DataTable
        {...ACCOUNTING_TABLE_DEFAULTS}
        data={data}
        columns={columns}
        keyExtractor={(r) => `${r.companyId}-${r.accountId}-${r.linkNumber}`}
        emptyMessage="No open items."
        stickyHeader
      />
    </AccountingReportShell>
  );
};
